import {
    Conversation,
    Assistant,
    ChatHistoryLoadingState,
    CosmosDBHealth,
    AssistantsLoadingState,
    AuthClaims,
    UserConfig,
    ChatUploadLimits
} from "../api";

export type ChatFile = {
    file: { name: string };
    text: string;
    status: string;
    new: boolean;
    tokenCount?: number;
    error?: string;
};

// Only successfully extracted attachments are sent to the model or counted against limits.
export function isUsableChatFile(file: ChatFile): boolean {
    return file.status === "Success" && file.text !== "";
}

export interface AppState {
    isChatHistoryOpen: boolean;
    chatHistoryLoadingState: ChatHistoryLoadingState;
    isCosmosDBAvailable: CosmosDBHealth;
    chatHistory: Conversation[] | null;
    filteredChatHistory: Conversation[] | null;
    currentChat: Conversation | null;
    assistant: Assistant | null;
    assistants: Assistant[] | null;
    customAssistants: Assistant[] | null;
    sharedAssistants: Assistant[] | null;
    jtcAssistants: Assistant[] | null;
    assistantsLoadingState: AssistantsLoadingState;
    previousChat: Conversation | null;
    answerLoading: boolean;
    selectedAssistant: string;
    checkedAssistant: string;
    historyCount: number;
    historyCurrentCount: number;
    chatHistoryMoreLoadingState: ChatHistoryLoadingState;
    loginExpired: boolean;
    urlAssistant: string | null;
    files: ChatFile[];
    canCreateAssistants: boolean;
    assistantBuilderManagerGroupId: string | null;
    userAuthClaims: AuthClaims | null;
    userConfig: UserConfig | null;
    chatUploadLimits: ChatUploadLimits | null;
}

export type Action =
    | { type: "TOGGLE_CHAT_HISTORY" }
    | { type: "SET_COSMOSDB_STATUS"; payload: CosmosDBHealth }
    | { type: "UPDATE_CHAT_HISTORY_LOADING_STATE"; payload: ChatHistoryLoadingState }
    | { type: "UPDATE_CURRENT_CHAT"; payload: Conversation | null }
    | { type: "UPDATE_FILTERED_CHAT_HISTORY"; payload: Conversation[] | null }
    | { type: "UPDATE_CHAT_HISTORY"; payload: Conversation } // API Call
    | { type: "UPDATE_CHAT_TITLE"; payload: Conversation } // API Call
    | { type: "DELETE_CHAT_ENTRY"; payload: string } // API Call
    | { type: "DELETE_CHAT_HISTORY" } // API Call
    | { type: "DELETE_CURRENT_CHAT_MESSAGES"; payload: string } // API Call
    | { type: "FETCH_CHAT_HISTORY"; payload: Conversation[] | null } // API Call
    | { type: "PIN_CHAT_HISTORY"; payload: string } // API Call
    | { type: "UNPIN_CHAT_HISTORY"; payload: string } // API Call
    | { type: "UPDATE_ASSISTANT"; payload: Assistant | null }
    | { type: "UPDATE_ASSISTANT_BY_TYPE"; payload: string }
    | { type: "FETCH_ASSISTANTS"; payload: Assistant[] | null } // API Call
    | { type: "UPDATE_CUSTOM_ASSISTANTS"; payload: Assistant[] | null }
    | { type: "UPDATE_SHARED_ASSISTANTS"; payload: Assistant[] | null }
    | { type: "UPDATE_JTC_ASSISTANTS"; payload: Assistant[] | null }
    | { type: "UPDATE_ASSISTANTS_LOADING_STATE"; payload: AssistantsLoadingState }
    | { type: "TOGGLE_ANSWER_LOADING" }
    | { type: "SET_ANSWER_LOADING"; payload: boolean }
    | { type: "UPDATE_SELECTED"; payload: string }
    | { type: "UPDATE_CHECKED"; payload: string }
    | { type: "SET_HISTORY_COUNT"; payload: number }
    | { type: "SET_HISTORY_CURRENT_COUNT"; payload: number }
    | { type: "FETCH_MORE_CHAT_HISTORY"; payload: Conversation[] | null } // API Call
    | { type: "UPDATE_CHAT_HISTORY_MORE_LOADING_STATE"; payload: ChatHistoryLoadingState }
    | { type: "SET_LOGIN_EXPIRED"; payload: boolean }
    | { type: "SET_URL_ASSISTANT"; payload: string | null }
    | { type: "ADD_FILE"; payload: string }
    | { type: "ADD_CHAT_HISTORY_FILE"; payload: { name: string; text: string } }
    | { type: "SET_FILE_RESULT"; payload: { name: string; text: string; tokenCount: number } }
    | { type: "SET_FILE_ERROR"; payload: { name: string; error: string } }
    | { type: "REMOVE_FILE"; payload: number }
    | { type: "REMOVE_FAILED_FILES" }
    | { type: "REMOVE_ALL_FILES" }
    | { type: "UPDATE_ALL_FILES_NEW" }
    | { type: "MARK_FILES_NEW"; payload: number[] }
    | { type: "UPDATE_CAN_CREATE_ASSISTANTS"; payload: boolean }
    | { type: "SET_ASSISTANT_BUILDER_MANAGER_GROUP"; payload: string | null }
    | { type: "UPDATE_USER_AUTH_CLAIMS"; payload: AuthClaims }
    | { type: "UPDATE_USER_CONFIG"; payload: UserConfig | null }
    | { type: "UPDATE_CHAT_UPLOAD_LIMITS"; payload: ChatUploadLimits | null };
